import { CSSProperties, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  formatDate,
  Heading,
  NakedUl,
  SearchField,
  Text,
  useControlledField,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import { MetricOption, METRICS_OPTIONS } from '../constants';
import { useAnalyticsReportingShallowStore } from '../hooks/use-analytics-reporting-store';

export const Metrices = () => {
  const { t } = useTranslation('analytics-reporting');
  const { selectedMetrices = [], onSelectedMetricesBatchChange } = useAnalyticsReportingShallowStore(
    'selectedMetrices',
    'onSelectedMetricesBatchChange'
  );

  const selectAllCheckboxProps = useControlledField({
    onChange: (value) => {
      onSelectedMetricesBatchChange(
        METRICS_OPTIONS.map((metric) => metric.id),
        value
      );
    },
    type: 'checkbox',
    value: !!selectedMetrices.length,
  });

  const searchFieldProps = useFormField({
    type: 'text',
  });

  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value);

  const searchedResults = useMemo(() => {
    if (!debouncedSearchValue) return METRICS_OPTIONS;

    return METRICS_OPTIONS.filter(({ label }) => {
      return label.toLowerCase().includes(debouncedSearchValue.toLowerCase());
    });
  }, [debouncedSearchValue]);

  return (
    <>
      <Heading level={3}>{t('Select Metrics')}</Heading>
      <SearchField {...searchFieldProps} data-trackingid='analytics-reporting-search-metrics' name='search-metrics' />
      <NakedUl>
        {searchedResults.length > 1 && (
          <CheckboxField
            {...selectAllCheckboxProps}
            css={styles.selectAllCheckbox}
            indeterminate={selectedMetrices.length > 0 && selectedMetrices.length < METRICS_OPTIONS.length}
            name='select-all-metrics'
            label={t('Select All')}
            trackingId='analytics-reporting-select-all-metrics'
          />
        )}
        {searchedResults.length ? (
          searchedResults.map((metric) => (
            <RowItem key={metric.id} showCheckbox={METRICS_OPTIONS.length > 1} {...metric} />
          ))
        ) : (
          <Text color='subdued' size='medium' textAlign='center' style={{ padding: theme.spacing(2) }}>
            {t('No results found')}
          </Text>
        )}
      </NakedUl>
    </>
  );
};

interface RowItemProps extends MetricOption {
  disabled?: boolean;
  showCheckbox?: boolean;
}

const RowItem = ({ disabled, icon, id, label, show = true, showCheckbox, availableFrom }: RowItemProps) => {
  const { selectedMetrices, onSelectedMetricesChange } = useAnalyticsReportingShallowStore(
    'selectedMetrices',
    'onSelectedMetricesChange'
  );

  const checkboxProps = useControlledField({
    onChange: (value) => {
      onSelectedMetricesChange(id, value);
    },
    type: 'checkbox',
    value: selectedMetrices?.includes(id),
  });

  if (!show) return null;

  return (
    <li css={styles.listItem} key={id}>
      {showCheckbox ? (
        <CheckboxField
          {...checkboxProps}
          name={`metric-${id}`}
          label={
            <LabelWithIcon
              disabled={disabled}
              icon={icon}
              label={label}
              availableFrom={availableFrom}
              style={{ marginLeft: theme.spacing(1) }}
            />
          }
          trackingId={`analytics-reporting-metric-${id}`}
        />
      ) : (
        <LabelWithIcon disabled={disabled} icon={icon} label={label} availableFrom={availableFrom} />
      )}
    </li>
  );
};

interface LabelWithIconProps {
  disabled?: boolean;
  icon: IconName;
  label: string;
  style?: CSSProperties;
  availableFrom?: string;
}

const LabelWithIcon = ({ disabled, icon, label, style, availableFrom }: LabelWithIconProps) => {
  const { t } = useTranslation('analytics-reporting');
  const availableFromDate = availableFrom ? dayjs(availableFrom) : '';
  const formattedAvailableFromDate = formatDate(availableFromDate, 'MMM D');
  const formattedAvailableFromYear = formatDate(availableFromDate, 'YYYY');

  return (
    <div
      className={`label-with-icon ${disabled ? 'disabled' : ''}`}
      style={style}
      css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0) }}
    >
      <span
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Icon name={icon} size={16} />
        <Text as='span' size='large' weight='bold'>
          {label}
        </Text>
      </span>
      {availableFromDate && (
        <Text size='small' color='subdued'>
          <Trans t={t} values={{ formattedAvailableFromDate, formattedAvailableFromYear }}>
            Available from <strong>{{ formattedAvailableFromDate }}</strong>, {{ formattedAvailableFromYear }}
          </Trans>
        </Text>
      )}
    </div>
  );
};

const styles = {
  listItem: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(2)};

    > div {
      align-items: center;
    }

    :not(:last-of-type) {
      margin-bottom: ${theme.spacing(2)};
    }

    .label-with-icon {
      align-items: center;
      display: flex;
      align-items: flex-start;

      &.disabled {
        color: ${theme.colors.neutral20};

        span {
          color: inherit;
        }
      }
    }
  `,

  selectAllCheckbox: css`
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(1)};
    padding: ${theme.spacing(0, 2, 2)};
  `,
};
